import { Link, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";
import { motion } from "framer-motion";
import { Sparkle, Heartbeat, ShieldCheck, Compass, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const beliefs = [
  {
    icon: Compass,
    title: "Healthspan over engagement",
    body: "Every recommendation optimizes for healthy, capable years of life — never for screen time. If a feature doesn't remove a tap, a decision, or a worry, it doesn't ship.",
  },
  {
    icon: Heartbeat,
    title: "Adapt to the human, not the plan",
    body: "Bodies have bad weeks. Ikavio senses when you're strained and quietly shrinks the day to something you can actually do — then builds you back up. Consistency beats intensity, always.",
  },
  {
    icon: ShieldCheck,
    title: "Your data works only for you",
    body: "No ads, no data brokers, no employer dashboards. Your health data exists for one purpose: producing your personal guidance. Safety features stay free, forever.",
  },
];

export default function About() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-bg text-ink overflow-x-hidden" data-testid="about-page">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="about-brand-link">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </Link>
        <div className="flex items-center gap-3">
          <ThemePicker />
          <Link to="/login" data-testid="about-login-link" className="text-sm text-muted2 hover:text-ink transition-colors px-2 py-2">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 label-eyebrow bg-surface border border-border rounded-full px-3 py-1.5 mt-6">
            <Sparkle size={13} weight="fill" color="var(--brand)" /> Our story
          </div>
          <h1 className="font-display font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Why <span className="text-brand">Ikavio</span> exists
          </h1>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 space-y-5 text-base leading-relaxed text-muted2"
          data-testid="about-story"
        >
          <p>
            <strong className="text-ink">Ikavio comes from the Japanese 生き — "iki" — to live.</strong> Not survive.
            Not perform for a quarter and burn out. To live: with intention, energy, and the quiet confidence that
            comes from a body and mind that keep showing up for you.
          </p>
          <p>
            We started Ikavio because the health industry got stuck measuring the wrong thing. Trackers hand you a
            recovery score at 7am and then... nothing. A number without a decision. Fitness apps hand you a rigid
            12-week program that assumes every day of your life is identical. Neither survives contact with a real
            week — a bad night, a sick kid, a brutal deadline.
          </p>
          <p>
            <strong className="text-ink">Ikavio is built for the real week.</strong> It's an operating system for
            your healthspan: it learns your sleep, stress, recovery and life, then decides your single most optimal
            day — every day. When you're thriving, it progresses you. When you're strained, it catches you before
            the spiral (stress → bad sleep → inactivity → guilt) and quietly switches to a Minimum Viable Day: three
            tiny actions, pressure off, no guilt. That's not a feature — it's the whole philosophy.
          </p>
          <p>
            The goal was never a streak or a score. The goal is <strong className="text-ink">more healthy, capable
            years of life</strong> — and a daily practice light enough that you're still doing it a decade from now.
          </p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14"
          data-testid="about-beliefs"
        >
          <h2 className="font-display text-lg font-medium">What we believe</h2>
          <div className="mt-6 grid sm:grid-cols-1 gap-4">
            {beliefs.map((b) => (
              <div key={b.title} className="bg-surface border border-border rounded-2xl p-6 flex gap-4 items-start">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-bg border border-border flex items-center justify-center">
                  <b.icon size={20} color="var(--brand)" />
                </div>
                <div>
                  <h3 className="font-medium text-ink">{b.title}</h3>
                  <p className="text-sm text-muted2 leading-relaxed mt-1.5">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-14 bg-surface border border-border rounded-2xl p-8 text-center"
          data-testid="about-cta"
        >
          <h2 className="font-display text-lg font-medium">Start living with intention</h2>
          <p className="text-sm text-muted2 mt-2 max-w-md mx-auto leading-relaxed">
            Every new member gets a 7-day full Pro trial. Bring the wearable you already own, or none at all.
          </p>
          <button
            data-testid="about-getstarted-btn"
            onClick={() => navigate(user ? "/app" : "/register")}
            className="mt-5 inline-flex items-center gap-2 text-sm bg-ink text-bg hover:bg-brand transition-colors rounded-full px-6 py-3 font-medium"
          >
            Get started <ArrowRight size={16} />
          </button>
        </motion.section>
      </main>

      <footer className="border-t border-border py-8 text-center text-sm text-muted2">
        <div className="flex items-center justify-center gap-5 flex-wrap px-6">
          <span>Ikavio — Optimizing for healthy, capable years of life.</span>
          <Link to="/glossary" className="text-brand hover:underline font-medium">Glossary</Link>
          <Link to="/privacy" className="text-brand hover:underline font-medium">Privacy</Link>
          <Link to="/terms" className="text-brand hover:underline font-medium">Terms</Link>
        </div>
      </footer>
    </div>
  );
}
